import { FunctionComponent as F, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getBookFormats,
  getBooks,
  getPlatforms,
  getPosts,
  postNewBookRef,
  postNewPostRef,
  postNewPost } from "../../core/http/ref";
import { IOption } from "@protoplan/types";
import { Dialog, DialogContent, DialogTitle, Stack, TextField, TextFieldProps } from "@mui/material";
import Button from "../UI/Button/Button";
import EditableDropdown from "../UI/Input/EditableDropdown";
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import ControlledCreatableAutocomplete from "../UI/Input/ControlledCreatableAutocomplete";
import ControlledAutocomplete from "../UI/Input/ControlledAutocomplete";
import { MM_INDEX } from "../../core/router/paths";



interface IParams
{
  topicId?: string
}

const AddRef: F = () =>
{
  const navigate = useNavigate();
  const params: IParams = useParams();
  const topicId = Number(params.topicId);

  const typeOptions: IOption[] = [
    { label: "book", value: 0 },
    { label: "post/article", value: 1 },
    { label: "audio/video", value: 2 }];
  const [typeValue, setTypeValue] = useState(0);

  const [allBooks, setAllBooks] = useState<{ bookId: number, title: string, bookFormatId: number }[]>([]);

  useEffect(() =>
  {
    if(typeValue === 0 && !allBooks.length)
    {
      getBooks()
        .then(books =>
        {
          if(!books)
            return;

          setAllBooks(books);
          const allBookTitles = books.map(b => b.title);
          const uniqueBookTitles = allBookTitles.filter(t => allBookTitles[allBookTitles.indexOf(t)] === t);

          setBookOptions(uniqueBookTitles.map((t, i) => ({ label: t, value: i })));
          setBookValue(books[6].bookId);
        });

      getBookFormats()
        .then(formats =>
        {
          setFormatOptions(formats?.map(f => ({ label: f.name, value: f.bookFormatId })));
          setFormatValue(1);
        });

      setNewPost(false);
    }
    else if(typeValue !== 0 && !platformOptions?.length)
    {
      getPlatforms()
        .then(platforms =>
        {
          if(!platforms?.length)
            return;

          setPlatformValue(platforms[0].platformId);
          setPlatformOptions(platforms.map(p => ({ label: p.name, value: p.platformId })));
        });
    }
  }, [typeValue]);



  // Book & web reference fields
  const [timestamp, setTimestamp] = useState<string | undefined>(undefined);


  // Book reference fields
  const [bookOptions, setBookOptions] = useState<IOption[] | undefined>(undefined);
  const [bookValue, setBookValue] = useState<number | undefined>(undefined);
  const [formatOptions, setFormatOptions] = useState<IOption[] | undefined>(undefined);
  const [formatValue, setFormatValue] = useState<number | undefined>(undefined);
  const [pageNo, setPageNo] = useState<undefined | string>(undefined);


  // Web reference fields
  const [platformOptions, setPlatformOptions] = useState<undefined | IOption[]>(undefined);
  const [platformValue, setPlatformValue] = useState<number | undefined>(undefined);
  const [posts, setPosts] = useState<undefined | { postId: number, title: string, date: Date }[]>(undefined);
  const [postOptions, setPostOptions] = useState<undefined | IOption[]>(undefined);
  const [postValue, setPostValue] = useState<number | null>(null);
  const [newPost, setNewPost] = useState<boolean>(false);
  const [url, setUrl] = useState<undefined | string>(undefined);
  const [date, setDate] = useState(new Date());

  useEffect(() =>
  {
    if(!platformValue)
      return;

    getPosts(platformValue)
      .then(posts0 =>
      {
        if(!posts0 || !posts0.length )
          return;

        setPosts(posts0);
        setPostOptions(posts0.map(p => ({ label: p.title, value: p.postId })));
        setPostValue(posts0[0].postId);
      });
  }, [platformValue]);


  if(!topicId)
  {
    navigate(MM_INDEX);
    return <></>;
  }

  // TODO: extract into date picker cpt
  const handleDateChange = (date: any | Date | number | string) =>
  {
    const currentDate = new Date();

    if(date > currentDate)
    {
      date.setDate(currentDate.getDate());
      setDate(currentDate);
    }
    else
    {
      setDate(date);
    }
  };



  const handleSubmit = async() =>
  {
    if(typeValue === 0)
    {
      if(!bookValue || !formatValue || !pageNo || !parseFloat(pageNo))
        return;

      const bookId = allBooks.filter(b =>
        b.title === bookOptions?.filter(o => o.value === bookValue)[0].label &&
        b.bookFormatId === formatValue)[0].bookId;

      await postNewBookRef(topicId, bookId, Number(pageNo), timestamp);
    }
    else
    {
      if(
        !platformValue ||
        !postValue ||
        (typeValue === 2 && (!timestamp || !timestamp.match(/(\d?\d:)?\d?\d:\d?\d/))))
        return;

      if(newPost)
      {
        const postTitle = postOptions?.filter(o => o.value === postValue)[0].label;

        if(!url  || !postTitle || !date)
          return;

        await postNewPost(platformValue, postValue, postTitle, date, url);
      }

      await postNewPostRef(topicId, postValue, timestamp);
    }

    navigate("../", { relative: 'path' });
  };



  return (
    <Dialog
      maxWidth='md'
      fullWidth
      PaperProps={{ sx: { p: '15px' }}}
      open={true}
      onClose={() => navigate("../", { relative: 'path' })}>

      <DialogTitle sx={{ ml: 3, fontSize: '30px' }}>Add a new reference</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ width: '750px', pt: 1 }}>

          <EditableDropdown
            initialValue={typeOptions.filter(o => o.value === typeValue)[0].value || 0}
            options={typeOptions}
            onChange={value => setTypeValue(Number(value))} />

          {typeValue === 0 ?
            <>

              {(bookOptions && bookValue !== undefined) ?
                <EditableDropdown
                  initialValue={bookValue}
                  options={bookOptions}
                  onChange={value => setBookValue(value)} /> :
                <></>}

              {(formatOptions && formatValue !== undefined) ?
                <EditableDropdown
                  initialValue={formatValue}
                  options={formatOptions}
                  onChange={value => setFormatValue(value)} /> :
                <></>}

              {(() =>
              {
                switch(formatValue)
                {
                case 1:
                case 2:
                  return (
                    <TextField
                      label='Page no.*'
                      variant='outlined'
                      size='small'
                      style={{ width: '750px' }}
                      onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setPageNo(event.target.value)}
                      error={pageNo && !parseFloat(pageNo) || false} />);
                case 3:
                  return (
                    <TextField
                      label='Timestamp (format (HH:)MM:SS)*'
                      variant='outlined'
                      size='small'
                      style={{ width: '750px' }}
                      onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setTimestamp(event.target.value)}
                      error={Boolean(timestamp && !timestamp.match(/(\d?\d:)?\d?\d:\d?\d/))} />);
                }
              })()}

            </> :
            <>

              {platformOptions ?
                <ControlledAutocomplete
                  label='Platform'
                  currentOption={platformOptions.filter(o => o.value === platformValue)[0]}
                  options={platformOptions}
                  onChangeCurrentOption={option => setPlatformValue(option?.value)} /> :
                <></>}

              {(postOptions && posts) ?
                <ControlledCreatableAutocomplete
                  label='Post title*'
                  currentOption={postOptions.filter(o => o.value === postValue)[0]}
                  handleChangeCurrentOption={value =>
                  {
                    const existingOptionValues = postOptions.map(o => o.value);
                    const existingPostIds = posts.map(p => p.postId);

                    if(value && !existingOptionValues.includes(value.value))
                    {
                      setNewPost(true);
                      const newValue = Math.max(...existingOptionValues) + 1;
                      setPostOptions(postOptions.concat([{ label: value.label, value: newValue }]));
                      setPostValue(newValue);
                    }
                    else if(value && !existingPostIds.includes(value.value))
                    {
                      setNewPost(true);
                      setPostValue(value.value);
                    }
                    else
                    {
                      setNewPost(false);
                      setPostValue(value?.value || null);
                    }
                  }}
                  options={postOptions} /> :
                <></>}

              {(typeValue === 2) ?
                <TextField
                  label='Timestamp (format (HH:)MM:SS)*'
                  variant='outlined'
                  size='small'
                  style={{ width: '750px' }}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setTimestamp(event.target.value)}
                  error={Boolean(timestamp && !timestamp.match(/(\d?\d:)?\d?\d:\d?\d/))} /> :
                <></>}

              {newPost ?
                <>
                  <TextField
                    label='URL*'
                    variant='outlined'
                    size='small'
                    style={{ width: '750px' }}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setUrl(event.target.value)}
                    error={url !== undefined && url === ""} />
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label='Date published*'
                      value={date}
                      onChange={handleDateChange}
                      renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) =>
                        <TextField {...params} size='small' helperText={params?.inputProps?.placeholder}/>}/>
                  </LocalizationProvider>
                </> :
                <></>}

            </>}

          <Button label='Submit' onClick={handleSubmit}/>
        </Stack>
      </DialogContent>
    </Dialog>);
};


export default AddRef;
